import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { ImportValidationResult, ImportedProduct } from '@/types/import.types';

interface ImportPreviewProps {
  result: ImportValidationResult;
  onBack: () => void;
  onImport: () => void;
  isImporting?: boolean;
}

export function ImportPreview({ result, onBack, onImport, isImporting }: ImportPreviewProps) {
  const totalValid = result.success.length;
  const totalWarnings = result.warnings.length;
  const totalErrors = result.errors.length;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-2xl font-bold text-green-600">{totalValid}</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Produits valides</p>
        </Card>

        <Card className="p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <span className="text-2xl font-bold text-yellow-600">{totalWarnings}</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Avertissements</p>
        </Card>

        <Card className="p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-2xl font-bold text-red-600">{totalErrors}</span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Erreurs</p>
        </Card>
      </div>

      {/* Preview Table */}
      <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className="text-left py-3 px-4 font-medium">Nom</th>
              <th className="text-left py-3 px-4 font-medium">Catégorie</th>
              <th className="text-left py-3 px-4 font-medium">Unité</th>
              <th className="text-left py-3 px-4 font-medium">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {/* Valid products */}
            {result.success.map((product, index) => (
              <PreviewRow
                key={`success-${index}`}
                name={product.nom}
                category={product.categorie}
                unit={product.unite}
                status="success"
                message="Prêt à importer"
              />
            ))}

            {/* Warnings */}
            {result.warnings.map((warning, index) => (
              <PreviewRow
                key={`warning-${index}`}
                name={warning.product.nom || '—'}
                category={warning.product.categorie || '—'}
                unit={warning.product.unite || '—'}
                status="warning"
                message={warning.message}
              />
            ))}

            {/* Errors */}
            {result.errors.map((error, index) => {
              const data = error.data as Record<string, unknown>;
              return (
                <PreviewRow
                  key={`error-${index}`}
                  name={String(data?.nom || data?.name || `Ligne ${error.row}`)}
                  category={String(data?.categorie || '—')}
                  unit={String(data?.unite || '—')}
                  status="error"
                  message={error.message}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Info message */}
      {totalValid > 0 && totalErrors > 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Seuls les {totalValid} produits valides seront importés. Les {totalErrors} produits avec erreurs seront ignorés.
        </p>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={isImporting}>
          Retour
        </Button>
        <Button
          onClick={onImport}
          disabled={totalValid === 0 || isImporting}
          loading={isImporting}
        >
          Importer {totalValid} produit{totalValid > 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}

interface PreviewRowProps {
  name: string;
  category: string;
  unit: string;
  status: 'success' | 'warning' | 'error';
  message: string;
}

function PreviewRow({ name, category, unit, status, message }: PreviewRowProps) {
  const statusConfig = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600 dark:text-green-400',
      iconColor: 'text-green-500',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-600 dark:text-yellow-400',
      iconColor: 'text-yellow-500',
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-600 dark:text-red-400',
      iconColor: 'text-red-500',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <tr className={config.bgColor}>
      <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{name}</td>
      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{category}</td>
      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{unit}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Icon className={cn('w-4 h-4', config.iconColor)} />
          <span className={cn('text-sm', config.textColor)}>{message}</span>
        </div>
      </td>
    </tr>
  );
}
